import { useState, useEffect, useCallback } from 'react';
import { API } from '../api/api';

function formatElapsed(startedAt) {
  const elapsed = Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
  const h = Math.floor(elapsed / 3600);
  const m = Math.floor((elapsed % 3600) / 60);
  const s = elapsed % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function formatCheckedInAt(startedAt) {
  return new Date(startedAt).toLocaleTimeString('da-DK', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Copenhagen',
  });
}

export default function PresenceView({ currentUserName }) {
  const [employees, setEmployees] = useState([]);
  const [tick, setTick] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const fetchPresence = useCallback((isManual = false) => {
    if (isManual) setRefreshing(true);
    API.fetchWorkdayPresence()
      .then(data => {
        setEmployees(data.data || []);
        setError(null);
      })
      .catch(() => setError('Kunne ikke hente tilstedeværelse'))
      .finally(() => {
        setLoading(false);
        if (isManual) setRefreshing(false);
      });
  }, []);

  // Initial fetch + poll every 30s
  useEffect(() => {
    fetchPresence(false);
    const poll = setInterval(() => fetchPresence(false), 30000);
    return () => clearInterval(poll);
  }, [fetchPresence]);

  // Tick every second to update elapsed times
  useEffect(() => {
    const timer = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50 p-4 pt-20 md:p-8">
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-semibold text-gray-900">Tilstædeværelse</h1>
            <button
              onClick={() => fetchPresence(true)}
              disabled={refreshing}
              title="Opdater tilstædeværelse"
              className="p-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
            >
              <svg className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
          <span className="text-sm text-gray-400">
            {employees.length} {employees.length === 1 ? 'medarbejder' : 'medarbejdere'} til stede
          </span>
        </div>

        {loading && (
          <div className="text-gray-400 text-sm">Henter data...</div>
        )}

        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
        )}

        {!loading && !error && employees.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <svg className="w-12 h-12 mb-4 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <p className="text-base font-medium">Ingen medarbejdere til stede</p>
            <p className="text-sm mt-1">Ingen har aktiveret arbejdstids-timeren endnu</p>
          </div>
        )}

        {!loading && employees.length > 0 && (
          <div className="space-y-3">
            {employees.map(emp => {
              const isMe = emp.name === currentUserName;
              return (
                <div
                  key={emp.Zuid}
                  className={`flex items-center justify-between p-4 rounded-xl border bg-white shadow-sm ${isMe ? 'border-[#537D5D]' : 'border-gray-200'}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="relative flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500" />
                    </span>
                    <div>
                      <p className={`font-medium text-gray-900 ${isMe ? 'text-[#537D5D]' : ''}`}>
                        {emp.name}{isMe && <span className="ml-2 text-xs font-normal text-gray-400">(dig)</span>}
                      </p>
                      <p className="text-xs text-gray-400">Mødt ind {formatCheckedInAt(emp.startedAt)}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-lg font-semibold text-gray-800">{formatElapsed(emp.startedAt)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
